import { uiPaper } from "shared/ui/uiSurface"
import {
  CallMerge,
  Close,
  Email,
  People,
  Phone,
} from "@mui/icons-material"
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import { FC, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  DuplicateGroup,
  useLazyGetClientDuplicatesQuery,
  useMergeClientsMutation,
} from "../../apis/appApi"

type Props = {
  open: boolean
  onClose: () => void
  onSuccess: (message: string) => void
}

export const ClientDuplicatesDialog: FC<Props> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation()
  const [fetchDuplicates, { data, isLoading, isFetching }] =
    useLazyGetClientDuplicatesQuery()
  const [mergeClients, { isLoading: isMerging }] = useMergeClientsMutation()

  // Track selected primary client per group (keyed by matchType+matchValue)
  const [selectedPrimary, setSelectedPrimary] = useState<
    Record<string, number>
  >({})
  // Track which groups have been merged
  const [mergedGroups, setMergedGroups] = useState<Set<string>>(new Set())
  // Confirmation dialog state
  const [confirmGroup, setConfirmGroup] = useState<DuplicateGroup | null>(null)

  const groupKey = (g: DuplicateGroup) => `${g.matchType}:${g.matchValue}`

  // Fetch duplicates when dialog opens
  const handleEnter = useCallback(() => {
    setSelectedPrimary({})
    setMergedGroups(new Set())
    setConfirmGroup(null)
    fetchDuplicates()
  }, [fetchDuplicates])

  const handleSelectPrimary = useCallback(
    (group: DuplicateGroup, clientId: number) => {
      setSelectedPrimary((prev) => ({
        ...prev,
        [groupKey(group)]: clientId,
      }))
    },
    [],
  )

  const handleMergeClick = useCallback((group: DuplicateGroup) => {
    setConfirmGroup(group)
  }, [])

  const handleConfirmMerge = useCallback(async () => {
    if (!confirmGroup) return
    const key = groupKey(confirmGroup)
    const primaryId = selectedPrimary[key]
    if (!primaryId) return

    const duplicateIds = confirmGroup.clients
      .map((c) => c.id)
      .filter((id) => id !== primaryId)

    try {
      const result = await mergeClients({
        primaryClientId: primaryId,
        duplicateClientIds: duplicateIds,
      }).unwrap()

      setMergedGroups((prev) => new Set(prev).add(key))
      setConfirmGroup(null)
      onSuccess(
        t("clients.duplicates.mergeSuccess", {
          count: result.merged,
          name: `${result.primaryClient.firstName} ${result.primaryClient.lastName}`,
        }),
      )

      // Refetch to get updated duplicate list
      fetchDuplicates()
    } catch {
      onSuccess(t("clients.duplicates.mergeError"))
    }
  }, [confirmGroup, selectedPrimary, mergeClients, onSuccess, t, fetchDuplicates])

  const matchTypeIcon = (type: string) => {
    switch (type) {
      case "name":
        return <People fontSize="small" />
      case "phone":
        return <Phone fontSize="small" />
      case "email":
        return <Email fontSize="small" />
      default:
        return <People fontSize="small" />
    }
  }

  const matchTypeLabel = (type: string) => {
    switch (type) {
      case "name":
        return t("clients.duplicates.matchType.name")
      case "phone":
        return t("clients.duplicates.matchType.phone")
      case "email":
        return t("clients.duplicates.matchType.email")
      default:
        return type
    }
  }

  const matchTypeColor = (type: string): "primary" | "secondary" | "warning" => {
    switch (type) {
      case "name":
        return "primary"
      case "phone":
        return "secondary"
      case "email":
        return "warning"
      default:
        return "primary"
    }
  }

  const groups = data?.groups ?? []
  const activeGroups = groups.filter((g) => !mergedGroups.has(groupKey(g)))

  return (
    <>
      <Dialog
        slotProps={{ paper: uiPaper("UI-DLV2-105") }}
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        TransitionProps={{ onEnter: handleEnter }}
      >
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <CallMerge />
              <Typography variant="h6" fontWeight={600}>
                {t("clients.duplicates.title")}
              </Typography>
            </Stack>
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          {isLoading || isFetching ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 200,
              }}
            >
              <CircularProgress />
            </Box>
          ) : activeGroups.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 200,
              }}
            >
              <Stack alignItems="center" spacing={1.5}>
                <People sx={{ fontSize: 48, color: "grey.400" }} />
                <Typography variant="subtitle1" color="text.secondary">
                  {t("clients.duplicates.noDuplicates")}
                </Typography>
              </Stack>
            </Box>
          ) : (
            <Stack spacing={3}>
              <Typography variant="body2" color="text.secondary">
                {t("clients.duplicates.description", {
                  count: activeGroups.length,
                })}
              </Typography>

              {activeGroups.map((group) => {
                const key = groupKey(group)
                const primaryId = selectedPrimary[key]

                return (
                  <Paper
                    key={key}
                    variant="outlined"
                    sx={{ p: 2, borderRadius: 2 }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mb: 1.5 }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          icon={matchTypeIcon(group.matchType)}
                          label={matchTypeLabel(group.matchType)}
                          color={matchTypeColor(group.matchType)}
                          size="small"
                          variant="outlined"
                        />
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          color="text.secondary"
                        >
                          {group.matchValue}
                        </Typography>
                        <Chip
                          label={t("clients.duplicates.clientCount", {
                            count: group.clients.length,
                          })}
                          size="small"
                          variant="filled"
                          sx={{ bgcolor: "grey.100" }}
                        />
                      </Stack>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<CallMerge />}
                        disabled={!primaryId || isMerging}
                        onClick={() => handleMergeClick(group)}
                      >
                        {t("clients.duplicates.merge")}
                      </Button>
                    </Stack>

                    <RadioGroup
                      value={primaryId ?? ""}
                      onChange={(e) =>
                        handleSelectPrimary(group, Number(e.target.value))
                      }
                    >
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell padding="checkbox">
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {t("clients.duplicates.primary")}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {t("clients.name")}
                              </TableCell>
                              <TableCell>{t("clients.email")}</TableCell>
                              <TableCell>
                                {t("clients.duplicates.phones")}
                              </TableCell>
                              <TableCell>
                                {t("clients.duplicates.orders")}
                              </TableCell>
                              <TableCell>
                                {t("clients.dateCreated")}
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {group.clients.map((client) => (
                              <TableRow
                                key={client.id}
                                hover
                                sx={{
                                  bgcolor:
                                    primaryId === client.id
                                      ? "action.selected"
                                      : undefined,
                                }}
                              >
                                <TableCell padding="checkbox">
                                  <FormControlLabel
                                    value={client.id}
                                    control={<Radio size="small" />}
                                    label=""
                                    sx={{ m: 0 }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" fontWeight={500}>
                                    {client.firstName} {client.lastName}
                                  </Typography>
                                  {client.organisation && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {client.organisation}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {client.email || "-"}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {client.phoneCount}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {client.orderCount}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {client.dateCreated
                                      ? new Date(
                                          client.dateCreated,
                                        ).toLocaleDateString()
                                      : "-"}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </RadioGroup>
                  </Paper>
                )
              })}
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} variant="outlined">
            {t("close")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Merge confirmation dialog */}
      <Dialog
        slotProps={{ paper: uiPaper("UI-DLV2-106") }}
        open={!!confirmGroup}
        onClose={() => setConfirmGroup(null)}
        maxWidth="sm"
      >
        <DialogTitle>{t("clients.duplicates.confirmTitle")}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t("clients.duplicates.confirmWarning")}
          </Alert>
          {confirmGroup && (
            <Typography variant="body2">
              {t("clients.duplicates.confirmMessage", {
                count: confirmGroup.clients.length - 1,
                primary: (() => {
                  const key = groupKey(confirmGroup)
                  const primaryId = selectedPrimary[key]
                  const pc = confirmGroup.clients.find(
                    (c) => c.id === primaryId,
                  )
                  return pc
                    ? `${pc.firstName} ${pc.lastName}`
                    : ""
                })(),
              })}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmGroup(null)} variant="outlined">
            {t("cancel")}
          </Button>
          <Button
            onClick={handleConfirmMerge}
            variant="contained"
            color="warning"
            disabled={isMerging}
            startIcon={
              isMerging ? <CircularProgress size={16} /> : <CallMerge />
            }
          >
            {t("clients.duplicates.confirmMerge")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
